import { NJButton, NJText } from '@engie-group/fluid-design-system-react';
import { UMR } from '../../urbanmap/_components';
import { planFreshForSet, plansUsing, summarize } from './model';
import { SetForm } from './SetForms';
import { SetSummary } from './SetSummary';
import { FAMILIES, type AnySet, type Family, type Project } from './types';
import { ACCENT, SaveBar, WARN } from './ui';

interface Props {
  project: Project;
  family: Family;
  selectedId: string;
  edit: boolean;
  canWrite: boolean;
  draft: AnySet | null;
  onSelectSet: (id: string) => void;
  onModify: () => void;
  onConsult: () => void;
  onCreate: () => void;
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
  onChange: (s: AnySet) => void;
  onSave: () => void;
  onCancel: () => void;
  onOpenPlan: (id: string) => void;
}

const FRESH: Record<string, { label: string; color: string }> = {
  fresh: { label: 'À jour', color: 'var(--nj-core-color-green-800, #1b6b30)' },
  stale: { label: 'À relancer', color: WARN },
  draft: { label: 'Non calculé', color: UMR.textSecondary },
};

export function SetEditor(p: Props) {
  const { project, family } = p;
  const meta = FAMILIES.find((f) => f.key === family)!;
  const sets = project.sets[family] as AnySet[];
  const current = sets.find((s) => s.id === p.selectedId) ?? sets[0];
  const used = plansUsing(project, family, current.id);
  const value = p.draft && p.draft.id === current.id ? p.draft : current;
  const canDelete = p.canWrite && used.length === 0 && sets.length > 1;
  const nameEmpty = value.name.trim() === '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: 16, maxWidth: meta.map === 'none' ? 760 : undefined }}>
      <div>
        <NJText as="h2" scale="lg" style={{ margin: 0 }}>{meta.title}</NJText>
        <NJText scale="sm" style={{ margin: '2px 0 0', color: UMR.textSecondary }}>{meta.question}</NJText>
      </div>

      {/* Liste des jeux de la famille */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }} role="listbox" aria-label={`Jeux — ${meta.label}`}>
        {sets.map((s) => {
          const n = plansUsing(project, family, s.id).length;
          const sel = s.id === current.id;
          return (
            <button key={s.id} type="button" role="option" aria-selected={sel} onClick={() => p.onSelectSet(s.id)}
              style={{ textAlign: 'left', cursor: 'pointer', padding: '8px 12px', background: sel ? 'var(--nj-core-color-teal-100, #e3f4f2)' : UMR.white, border: `1px solid ${sel ? ACCENT : UMR.border}`, borderLeft: `4px solid ${sel ? ACCENT : UMR.border}`, fontFamily: UMR.font, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 14, fontWeight: 700 }}>
                {s.name}<span style={{ fontWeight: 400, fontSize: 12, color: UMR.textSecondary }}>v{s.version}</span>
                <span style={{ marginLeft: 'auto', fontWeight: 400, fontSize: 12, color: n ? ACCENT : UMR.textSecondary }}>Utilisé par {n} plan{n > 1 ? 's' : ''}</span>
              </span>
              <span style={{ fontSize: 12, color: UMR.textSecondary }}>{summarize(s)}</span>
            </button>
          );
        })}
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          {p.canWrite && <NJButton label="Nouveau jeu" variant="secondary" scale="sm" onClick={p.onCreate} />}
          {p.canWrite && <NJButton label="Dupliquer" variant="secondary" emphasis="minimal" scale="sm" onClick={() => p.onDuplicate(current.id)} />}
          {p.canWrite && <NJButton label="Supprimer" variant="destructive" emphasis="minimal" scale="sm" disabled={!canDelete} onClick={() => p.onDelete(current.id)} />}
        </div>
        {!canDelete && <span style={{ fontSize: 12, color: UMR.textSecondary }}>{used.length ? 'Suppression impossible : ce jeu est utilisé par au moins un plan.' : 'Un projet garde au moins un jeu par famille.'}</span>}
      </div>

      {/* Jeu sélectionné : consultation ou édition */}
      <section aria-label="Jeu sélectionné" style={{ borderTop: `1px solid ${UMR.border}`, paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <NJText as="h3" scale="md" style={{ margin: 0 }}>{current.name} <span style={{ fontWeight: 400, color: UMR.textSecondary }}>v{current.version}</span></NJText>
          <div style={{ marginLeft: 'auto' }}>
            {p.edit
              ? <NJButton label="Consultation" variant="secondary" emphasis="minimal" scale="sm" onClick={p.onConsult} disabled={p.draft !== null} />
              : p.canWrite && <NJButton label="Modifier" variant="primary" emphasis="subtle" scale="sm" onClick={p.onModify} />}
          </div>
        </div>

        {p.edit && used.length > 0 && (
          <div style={{ fontSize: 12, color: WARN, background: 'var(--nj-core-color-orange-100, #fdf0e7)', padding: '6px 10px' }}>
            Confirmer rendra obsolètes les plans qui utilisent ce jeu (ci-dessous) : leurs sections concernées seront à recalculer.
          </div>
        )}

        {p.edit ? (
          <>
            <SetForm value={value} onChange={p.onChange} />
            <SaveBar dirty={p.draft !== null && !nameEmpty} onCancel={p.onCancel} onSave={p.onSave} />
            {nameEmpty && <span style={{ fontSize: 12, color: WARN, textAlign: 'right' }}>Nommez le jeu pour pouvoir le confirmer.</span>}
          </>
        ) : (
          <SetSummary set={current} />
        )}
      </section>

      {/* Traçabilité : plans qui utilisent ce jeu + fraîcheur */}
      <section aria-label="Plans utilisant ce jeu" style={{ borderTop: `1px solid ${UMR.border}`, paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <NJText as="h3" scale="md" style={{ margin: 0 }}>Utilisé par {used.length} plan{used.length > 1 ? 's' : ''} de dev</NJText>
        {used.length === 0 && <span style={{ fontSize: 13, color: UMR.textSecondary }}>Aucun plan n’utilise encore ce jeu.</span>}
        {used.map((pl) => {
          const f = planFreshForSet(pl, family, current);
          return (
            <div key={pl.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, border: `1px solid ${UMR.border}`, padding: '6px 10px' }}>
              <span style={{ flex: 1 }}>{pl.name}</span>
              <span style={{ fontSize: 11, color: FRESH[f].color, whiteSpace: 'nowrap' }}>● {FRESH[f].label}</span>
              <NJButton label="Voir le plan" variant="secondary" emphasis="minimal" scale="sm" onClick={() => p.onOpenPlan(pl.id)} />
            </div>
          );
        })}
      </section>
    </div>
  );
}
